import React, { useMemo } from 'react';
import Select, { components as rsComponents } from 'react-select';

const ChevronDownIcon = () => (
	<svg className="chevron-down-icon" viewBox="0 0 22 22">
		<path
			d="M6 9l5 5 5-5"
			stroke="currentColor"
			strokeWidth="1"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const ClearIcon = () => (
	<svg
		className="ats-multi-dropdown-grouped__clear-icon"
		viewBox="0 0 14 14"
		aria-hidden="true">
		<path
			d="M3.5 3.5l7 7M10.5 3.5l-7 7"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
		/>
	</svg>
);

const SelectedCountPill = ({ count, onClear, isClearable = true }) => {
	if (!count) return null;
	return (
		<span className="ats-multi-dropdown-grouped__count-pill">
			<span className="ats-multi-dropdown-grouped__count">{count}</span>
			{isClearable && (
				<button
					type="button"
					className="ats-multi-dropdown-grouped__clear"
					aria-label="Clear all selected"
					// `onMouseDown` (not `onClick`) is what react-select listens
					// to on the control; preventing default + stopping
					// propagation keeps the menu from toggling when clearing.
					onMouseDown={e => {
						e.preventDefault();
						e.stopPropagation();
						onClear?.();
					}}
					onTouchEnd={e => {
						e.preventDefault();
						e.stopPropagation();
						onClear?.();
					}}>
					<ClearIcon />
				</button>
			)}
		</span>
	);
};

const IndicatorsContainerWithCount = ({ children, ...props }) => {
	const { selectProps, clearValue } = props;
	const value = selectProps?.value;
	const count = Array.isArray(value) ? value.length : 0;
	return (
		<rsComponents.IndicatorsContainer {...props}>
			<SelectedCountPill
				count={count}
				isClearable={Boolean(selectProps?.isClearable)}
				onClear={clearValue}
			/>
			{children}
		</rsComponents.IndicatorsContainer>
	);
};

export const defaultGetOptionValue = option => option?.value;

/**
 * Render-time grouping. Reads `value` via `getValue()` on every render, so
 * the "Selected" section is always in sync — even with `AsyncSelect`'s
 * internal `loadOptions` cache, which would otherwise serve stale grouped
 * results when the selection changes.
 *
 * - When the menu children are flat `Option` elements (default for async
 *   loads), they're split into a "Selected" header + their options on top
 *   and the remaining options below.
 * - When the menu children are already `Group` elements (sync mode passes
 *   pre-grouped `options`), they pass through untouched so the native
 *   grouping/styling keeps working.
 * - Non-option children (`LoadingMessage`, `NoOptionsMessage`, etc.) always
 *   pass through.
 */
const MenuListWithSelectedGroup = ({ children, ...props }) => {
	const { getValue, selectProps } = props;
	const value = getValue();
	const childArray = React.Children.toArray(children);

	if (!Array.isArray(value) || value.length === 0 || childArray.length === 0) {
		return <rsComponents.MenuList {...props}>{children}</rsComponents.MenuList>;
	}

	const getOptionValue = selectProps?.getOptionValue || defaultGetOptionValue;
	const selectedKeys = new Set(value.map(v => getOptionValue(v)));

	const selectedItems = [];
	const others = [];
	const tail = [];

	for (const child of childArray) {
		const data = child?.props?.data;
		const key = data ? getOptionValue(data) : undefined;
		if (key === undefined) {
			tail.push(child);
			continue;
		}
		if (selectedKeys.has(key)) {
			selectedItems.push(child);
		} else {
			others.push(child);
		}
	}

	// Children were not flat options (e.g. already grouped, or empty state)
	// — render unchanged so native grouping continues to work.
	if (selectedItems.length === 0 && others.length === 0) {
		return <rsComponents.MenuList {...props}>{children}</rsComponents.MenuList>;
	}

	const selectedLabel = selectProps?.selectedGroupLabel || 'Selected';
	const availableLabel = selectProps?.availableGroupLabel || 'Options';

	return (
		<rsComponents.MenuList {...props}>
			{selectedItems.length > 0 && (
				<div className="ats-multi-dropdown-grouped__menu-heading">
					{selectedLabel}
				</div>
			)}
			{selectedItems}
			{selectedItems.length > 0 && others.length > 0 && (
				<div className="ats-multi-dropdown-grouped__menu-heading ats-multi-dropdown-grouped__menu-heading--with-divider">
					{availableLabel}
				</div>
			)}
			{others}
			{tail}
		</rsComponents.MenuList>
	);
};

export const groupedDefaultComponents = {
	IndicatorSeparator: () => null,
	// Built-in clear is suppressed because the pill renders its own `x` next
	// to the count whenever `isClearable` is enabled.
	ClearIndicator: () => null,
	DropdownIndicator: props => (
		<rsComponents.DropdownIndicator {...props}>
			<ChevronDownIcon />
		</rsComponents.DropdownIndicator>
	),
	IndicatorsContainer: IndicatorsContainerWithCount,
	MenuList: MenuListWithSelectedGroup
};

/**
 * Splits a flat options array into two groups: items that are currently
 * selected and the rest. Empty groups are omitted so the menu doesn't show
 * empty headings. Shared by the sync and async variants.
 */
export function buildGroupedOptions({
	options,
	value,
	getOptionValue = defaultGetOptionValue,
	selectedGroupLabel = 'Selected',
	availableGroupLabel = 'Options'
}) {
	const selectedValues = Array.isArray(value) ? value : [];
	const selectedKeys = new Set(selectedValues.map(v => getOptionValue(v)));

	const available = [];
	const selectedFromOptions = new Map();

	for (const option of options || []) {
		const key = getOptionValue(option);
		if (selectedKeys.has(key)) {
			selectedFromOptions.set(key, option);
		} else {
			available.push(option);
		}
	}

	// Pin every currently-selected item at the top, even ones missing from
	// the freshly loaded options (relevant for async usage where the user
	// may have searched something that doesn't match their selection).
	const selected = selectedValues.map(
		v => selectedFromOptions.get(getOptionValue(v)) || v
	);

	const groups = [];
	if (selected.length > 0) {
		groups.push({ label: selectedGroupLabel, options: selected });
	}
	if (available.length > 0) {
		groups.push({ label: availableGroupLabel, options: available });
	}
	return groups;
}

/**
 * Multi-select dropdown that pins the currently selected options to the top
 * of the menu, grouped under a "Selected" heading, while the remaining
 * options stay below under "Options". Built on top of react-select's native
 * grouped options support (https://react-select.com/home#grouped).
 */
const fitMenuToContentStyles = {
	menu: base => ({
		...base,
		width: 'max-content',
		minWidth: '100%'
	}),
	option: base => ({
		...base,
		whiteSpace: 'nowrap'
	})
};

export const purpleSelectStyles = {
	control: (base, state) => ({
		...base,
		backgroundColor: '#FFFFFF',
		...(state.isFocused && {
			borderColor: '#6938EF',
			boxShadow: '0 0 0 1px #6938EF',
			backgroundColor: '#F4F3FF'
		}),
		'&:hover': {
			...base['&:hover'],
			borderColor: state.isFocused ? '#6938EF' : base.borderColor,
			backgroundColor: '#F4F3FF'
		}
	}),
	option: (base, state) => ({
		...base,
		...(state.isFocused && {
			backgroundColor: '#F4F3FF'
		}),
		':active': {
			...base[':active'],
			backgroundColor: '#EBE9FE'
		}
	}),
	multiValue: base => ({
		...base,
		backgroundColor: '#F4F3FF',
		borderRadius: '4px'
	}),
	multiValueLabel: base => ({
		...base,
		backgroundColor: '#F4F3FF',
		color: '#6938EF',
		fontSize: '12px',
		fontWeight: 500
	}),
	multiValueRemove: base => ({
		...base,
		backgroundColor: '#F4F3FF',
		color: '#6938EF',
		':hover': {
			backgroundColor: '#EBE9FE',
			color: '#6938EF'
		}
	})
};

function MultiDropdownGrouped({
	options = [],
	value,
	getOptionValue = defaultGetOptionValue,
	getOptionLabel,
	selectedGroupLabel = 'Selected',
	availableGroupLabel = 'Options',
	components: extraComponents,
	fitMenuToContent = false,
	styles: customStyles,
	classNames: customClassNames,
	...rest
}) {
	const groupedOptions = useMemo(
		() =>
			buildGroupedOptions({
				options,
				value,
				getOptionValue,
				selectedGroupLabel,
				availableGroupLabel
			}),
		[options, value, getOptionValue, selectedGroupLabel, availableGroupLabel]
	);

	const mergedComponents = useMemo(
		() => ({ ...groupedDefaultComponents, ...(extraComponents || {}) }),
		[extraComponents]
	);

	const mergedStyles = useMemo(() => {
		const mergeStyleFn = (key, base, state) => {
			let next = purpleSelectStyles[key]?.(base, state) ?? base;
			if (fitMenuToContent && fitMenuToContentStyles[key]) {
				next = fitMenuToContentStyles[key](next, state);
			}
			return customStyles?.[key] ? customStyles[key](next, state) : next;
		};

		return {
			...customStyles,
			control: (base, state) => mergeStyleFn('control', base, state),
			option: (base, state) => mergeStyleFn('option', base, state),
			multiValue: (base, state) => mergeStyleFn('multiValue', base, state),
			multiValueLabel: (base, state) =>
				mergeStyleFn('multiValueLabel', base, state),
			multiValueRemove: (base, state) =>
				mergeStyleFn('multiValueRemove', base, state),
			...(fitMenuToContent
				? { menu: (base, state) => mergeStyleFn('menu', base, state) }
				: {})
		};
	}, [customStyles, fitMenuToContent]);

	const mergedClassNames = useMemo(() => {
		if (!fitMenuToContent) {
			return customClassNames;
		}

		return {
			...customClassNames,
			menu: state => {
				const extra = customClassNames?.menu?.(state) || '';
				return `ats-multi-dropdown-grouped__menu--fit-content ${extra}`.trim();
			}
		};
	}, [customClassNames, fitMenuToContent]);

	return (
		<Select
			isMulti
			options={groupedOptions}
			value={value}
			getOptionLabel={getOptionLabel}
			getOptionValue={getOptionValue}
			components={mergedComponents}
			hideSelectedOptions={false}
			isClearable
			classNamePrefix="react-select"
			styles={mergedStyles}
			classNames={mergedClassNames}
			{...rest}
		/>
	);
}

export default MultiDropdownGrouped;
